import type { AppMessages, Dictionary } from '../models'

import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { FeatureIdEnum, LocaleEnum, TimePeriodEnum } from '../enums'

const supportLink = 'https://support.empathy.co'

const APP_MESSAGES_EN: AppMessages = {
  title: 'PLAY-MERCHANT',
  instanceSelector: {
    greeting: 'Hi',
    tab: {
      tooltip: 'Instances',
    },
    search: {
      placeholder: 'Search your instance',
      noInstancesFound: 'No instances found',
    },
    showAll: 'Show all ({counter})',
    showLess: 'Show less',
    noInstancesFound: {
      default:
        'You don’t have permission to access any instances.<br>Contact your administrator or Empathy support to request access.',
      partner:
        'You haven’t any instances yet.<br>Click <b>Request instance</b> to create a new instance.',
    },
    requestInstance: 'Request instance',
  },
  domains: {
    [DomainEnum.SEARCH]: 'search',
    [DomainEnum.BROWSE]: 'discovery',
  },
  common: 'common',
  user: {
    documentationTitle: 'Playboard docs',
    documentationLink: 'https://docs.empathy.co/play-with-empathy-platform/',
  },
  new: 'new',
  features: {
    [FeatureIdEnum.FEEDBACK]: 'Feedback',

    [FeatureIdEnum.HIGHLIGHTS]: 'My Highlights',

    [FeatureIdEnum.INSIGHTS]: 'Insights',
    [FeatureIdEnum.NOW]: 'Now',
    [FeatureIdEnum.ORIGINS]: 'Origins',
    [FeatureIdEnum.KEYWORDS]: 'Terms',
    [FeatureIdEnum.CATEGORIES]: 'Categories',
    [FeatureIdEnum.OPPORTUNITIES]: 'Opportunities',
    [FeatureIdEnum.OVERALL]: {
      [DomainEnum.SEARCH]: 'Overall',
      [DomainEnum.BROWSE]: 'Browse Metrics',
    },
    [FeatureIdEnum.QUERIES]: 'Queries',
    [FeatureIdEnum.CONVERSATIONAL]: 'Conversational',
    [FeatureIdEnum.SEMANTICS]: 'Semantics',
    [FeatureIdEnum.RELATED_PROMPTS_ANALYTICS]: 'Prompt Metrics',
    [FeatureIdEnum.TOOL_REPORTING]: 'Tool Reporting',

    [FeatureIdEnum.PLAY]: 'Play',
    [FeatureIdEnum.ROOT_QUERY]: 'Query',
    [FeatureIdEnum.EQUALIZE]: 'Equalize',
    [FeatureIdEnum.SYNONYM]: 'Synonymize',
    [FeatureIdEnum.BLACKLIST]: 'Blacklist',
    [FeatureIdEnum.PROMOTION]: 'Promotion',
    [FeatureIdEnum.REDIRECTION]: 'Redirection',
    [FeatureIdEnum.CONFIGURATION]: {
      [DomainEnum.SEARCH]: 'Results Ranking',
      [DomainEnum.BROWSE]: 'Configuration',
    },
    [FeatureIdEnum.RELATED_TAGS]: 'Related Tags',
    [FeatureIdEnum.NEXT_QUERIES]: 'Next Queries',
    [FeatureIdEnum.RELATED_PROMPTS]: 'Related Prompts',

    [FeatureIdEnum.BROWSE]: 'Browse',

    [FeatureIdEnum.EXPLAIN]: 'Explain',

    [FeatureIdEnum.FACETS]: 'Facets',

    [FeatureIdEnum.AUDIT]: 'Activity Stream',

    [FeatureIdEnum.INDEX]: 'Index Management',

    [FeatureIdEnum.USER_ROLE_MANAGEMENT]: 'Users Management',

    [FeatureIdEnum.INSTANCE_MANAGEMENT]: 'Instance Management',

    [FeatureIdEnum.CLIENT_CONFIG]: 'Client Config',
    [FeatureIdEnum.ENDPOINT_CONFIG]: 'Endpoint Config',
    [FeatureIdEnum.FEATURE_CONFIG]: 'Feature Config',

    [FeatureIdEnum.INDEX_CLIENT]: 'Index Client',
    [FeatureIdEnum.INDEX_FEED_CONFIGURATION]: 'Index Feed Configuration',
    [FeatureIdEnum.INDEX_SCHEMA_CONFIGURATION]: 'Index Schema Configuration',

    [FeatureIdEnum.ACCOUNT]: 'Account',
  },
  routes: {
    home: 'Playboard',

    create: 'New',
    update: 'Edit',
    bulkEdit: 'Multi-Editing',

    createEqualize: 'New equalize',
    updateEqualize: 'Edit equalize',
    createSynonym: 'New synonym',
    updateSynonym: 'Edit synonym',
    createBlacklist: 'New blacklist',
    updateBlacklist: 'Edit blacklist',
    createPromotion: 'New promotion',
    updatePromotion: 'Edit promotion',
    createRedirection: 'New redirection',
    updateRedirection: 'Edit redirection',
    createConfiguration: {
      [DomainEnum.SEARCH]: 'New results ranking',
      [DomainEnum.BROWSE]: 'New configuration',
    },
    updateConfiguration: {
      [DomainEnum.SEARCH]: 'Update results ranking',
      [DomainEnum.BROWSE]: 'Update configuration',
    },
    createRelatedTags: 'New related tags',
    updateRelatedTags: 'Update related tags',
    createNextQueries: 'New next queries',
    updateNextQueries: 'Update next queries',

    previewRelatedPrompts: 'Preview',
    overviewRelatedPrompts: 'Overview',

    createInstance: 'New instance',
    updateInstance: 'Update instance',
    goToAllInstances: 'Go to all Instances',

    explainSearch: 'Search',
    explainQueryAnalysis: 'Query Analysis',
    explainProductAnalysis: 'Product Analysis',

    logout: 'Log out',
  },
  fullscreen: {
    close: 'Close fullscreen mode',
    open: 'Open fullscreen mode',
  },
  pageNotFound: {
    introduction: 'We’re sorry but the page you’re looking for no longer exists.',
    redirection: `Visit the {homeUrl} or <a href="${supportLink}" target="_blank">contact us</a> about the problem.`,
    homepage: 'homepage',
  },
  date: {
    time: {
      periods: {
        [TimePeriodEnum.AM]: 'AM',
        [TimePeriodEnum.PM]: 'PM',
      },
    },
    year: {
      singular: 'year',
      plural: 'years',
    },
    month: {
      singular: 'month',
      plural: 'months',
    },
    day: {
      singular: 'day',
      plural: 'days',
    },
    hour: {
      singular: 'hour',
      plural: 'hours',
    },
    elapsedTime: {
      lessThan: 'Less than',
      zero: 'None',
    },
  },
  copyToClipboard: 'Copy to clipboard',
  inputs: {
    keywords: {
      duplicatedKeywordsHelpMessage: 'Already existing values not added.',
    },
    titleInput: {
      emptyError: 'Please enter a {label}',
    },
  },
  and: 'and',
  uploadFile: {
    dragDrop: 'Drag & drop or ',
    button: 'click here',
    upload: 'to upload your file',
    fileUpload: 'Your file has been',
    successfully: 'successfully uploaded.',
    formatError: 'Incorrect file format. Supported formats are {supportedFormats}.',
  },
  switch: {
    enabled: 'On',
    disabled: 'Off',
  },
  state: {
    active: 'Active',
    inactive: 'Inactive',
  },
  darkMode: 'Dark Mode',
}

const APP_MESSAGES_ES: AppMessages = {
  title: 'PLAY-MERCHANT',
  instanceSelector: {
    greeting: '¡Hola',
    tab: {
      tooltip: 'Instancias',
    },
    search: {
      placeholder: 'Busca tu instancia',
      noInstancesFound: 'No se han encontrado instancias',
    },
    showAll: 'Mostrar todas ({counter})',
    showLess: 'Mostrar menos',
    noInstancesFound: {
      default:
        'No tienes permisos para acceder a ninguna instancia.<br>Ponte en contacto con el administrador de tu cuenta o con el servicio de soporte de Empathy para solicitar acceso.',
      partner:
        'No tienes ninguna instancia creada.<br>Haz clic en <b>Solicitar instancia</b> para crear una instancia.',
    },
    requestInstance: 'Solicitar instancia',
  },
  domains: {
    [DomainEnum.SEARCH]: 'búsqueda',
    [DomainEnum.BROWSE]: 'navegación',
  },
  common: 'común',
  user: {
    documentationTitle: 'Playboard docs',
    documentationLink: 'https://docs.empathy.co/play-with-empathy-platform/',
  },
  new: '',
  features: {
    [FeatureIdEnum.FEEDBACK]: 'Feedback',

    [FeatureIdEnum.HIGHLIGHTS]: 'My Highlights',

    [FeatureIdEnum.INSIGHTS]: 'Insights',
    [FeatureIdEnum.NOW]: 'Now',
    [FeatureIdEnum.ORIGINS]: 'Origins',
    [FeatureIdEnum.KEYWORDS]: 'Terms',
    [FeatureIdEnum.CATEGORIES]: 'Categories',
    [FeatureIdEnum.OPPORTUNITIES]: 'Opportunities',
    [FeatureIdEnum.OVERALL]: {
      [DomainEnum.SEARCH]: 'Overall',
      [DomainEnum.BROWSE]: 'Browse Metrics',
    },
    [FeatureIdEnum.QUERIES]: 'Queries',
    [FeatureIdEnum.CONVERSATIONAL]: 'Conversational',
    [FeatureIdEnum.SEMANTICS]: 'Semantics',
    [FeatureIdEnum.RELATED_PROMPTS_ANALYTICS]: 'Prompt Metrics',
    [FeatureIdEnum.TOOL_REPORTING]: 'Tool Reporting',

    [FeatureIdEnum.PLAY]: 'Play',
    [FeatureIdEnum.ROOT_QUERY]: 'Query',
    [FeatureIdEnum.EQUALIZE]: 'Equalize',
    [FeatureIdEnum.SYNONYM]: 'Synonymize',
    [FeatureIdEnum.BLACKLIST]: 'Blacklist',
    [FeatureIdEnum.PROMOTION]: 'Promotion',
    [FeatureIdEnum.REDIRECTION]: 'Redirection',
    [FeatureIdEnum.CONFIGURATION]: {
      [DomainEnum.SEARCH]: 'Results ranking',
      [DomainEnum.BROWSE]: 'Configuración',
    },
    [FeatureIdEnum.RELATED_TAGS]: 'Related tags',
    [FeatureIdEnum.NEXT_QUERIES]: 'Next queries',
    [FeatureIdEnum.RELATED_PROMPTS]: 'Related Prompts',

    [FeatureIdEnum.BROWSE]: 'Browse',

    [FeatureIdEnum.EXPLAIN]: 'Explain',

    [FeatureIdEnum.FACETS]: 'Facets',

    [FeatureIdEnum.AUDIT]: 'Activity Stream',

    [FeatureIdEnum.INDEX]: 'Index management',

    [FeatureIdEnum.USER_ROLE_MANAGEMENT]: 'Gestión de usuarios',

    [FeatureIdEnum.INSTANCE_MANAGEMENT]: 'Gestión de instancia',

    [FeatureIdEnum.CLIENT_CONFIG]: 'Client Config',
    [FeatureIdEnum.ENDPOINT_CONFIG]: 'Endpoint Config',
    [FeatureIdEnum.FEATURE_CONFIG]: 'Feature Config',

    [FeatureIdEnum.INDEX_CLIENT]: 'Index Client',
    [FeatureIdEnum.INDEX_FEED_CONFIGURATION]: 'Index Feed Configuration',
    [FeatureIdEnum.INDEX_SCHEMA_CONFIGURATION]: 'Index Schema Configuration',

    [FeatureIdEnum.ACCOUNT]: 'Cuenta',
  },
  routes: {
    home: 'Playboard',

    create: 'Nuevo',
    update: 'Editar',
    bulkEdit: 'Edición múltiple',

    createEqualize: 'Nuevo equalize',
    updateEqualize: 'Editar equalize',
    createSynonym: 'Nuevo sinónimo',
    updateSynonym: 'Editar sinónimo',
    createBlacklist: 'Nueva blacklist',
    updateBlacklist: 'Editar blacklist',
    createPromotion: 'Nueva promotion',
    updatePromotion: 'Editar promotion',
    createRedirection: 'Nueva redirection',
    updateRedirection: 'Editar redirection',
    createConfiguration: {
      [DomainEnum.SEARCH]: 'Nuevo results ranking',
      [DomainEnum.BROWSE]: 'Nueva configuración',
    },
    updateConfiguration: {
      [DomainEnum.SEARCH]: 'Editar results ranking',
      [DomainEnum.BROWSE]: 'Editar configuración',
    },
    createRelatedTags: 'Nuevas related tags',
    updateRelatedTags: 'Editar related tags',
    createNextQueries: 'Nuevas next queries',
    updateNextQueries: 'Editar next queries',

    previewRelatedPrompts: '',
    overviewRelatedPrompts: '',

    createInstance: 'Nueva instancia',
    updateInstance: 'Editar instancia',
    goToAllInstances: 'Go to all Instances',

    explainSearch: '',
    explainQueryAnalysis: '',
    explainProductAnalysis: '',

    logout: 'Cerrar sesión',
  },
  fullscreen: {
    close: 'Cerrar modo pantalla completa',
    open: 'Abrir modo pantalla completa',
  },
  pageNotFound: {
    introduction: 'Lo sentimos pero la página que está buscando ya no existe.',
    redirection: `Visita la {homeUrl} o <a href="${supportLink}" target="_blank">contáctanos</a> sobre el problema.`,
    homepage: 'página de inicio',
  },
  date: {
    time: {
      periods: {
        [TimePeriodEnum.AM]: 'AM',
        [TimePeriodEnum.PM]: 'PM',
      },
    },
    year: {
      singular: 'año',
      plural: 'años',
    },
    month: {
      singular: 'mes',
      plural: 'meses',
    },
    day: {
      singular: 'día',
      plural: 'días',
    },
    hour: {
      singular: 'hora',
      plural: 'horas',
    },
    elapsedTime: {
      lessThan: 'Menos de',
      zero: 'Ninguno',
    },
  },
  copyToClipboard: 'Copiar al portapapeles',
  inputs: {
    keywords: {
      duplicatedKeywordsHelpMessage: '',
    },
    titleInput: {
      emptyError: '',
    },
  },
  and: 'y',
  uploadFile: {
    dragDrop: '',
    button: '',
    upload: '',
    fileUpload: '',
    successfully: '',
    formatError: '',
  },
  switch: {
    enabled: 'Encendido',
    disabled: 'Apagado',
  },
  state: {
    active: 'Activo',
    inactive: 'Inactivo',
  },
  darkMode: '',
}

export const APP_MESSAGES: Dictionary<{ app: AppMessages }> = {
  [LocaleEnum.ENGLISH]: {
    app: APP_MESSAGES_EN,
  },
  [LocaleEnum.SPANISH]: {
    app: APP_MESSAGES_ES,
  },
}
